import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { uniqueId } from 'lodash-es';
import { ArrowDown16 } from '@honeybook/icons';
import HBInput from '../HBInput/HBInput';
import HBFlex from '../HBFlex/HBFlex';
import { HBList } from '../HBList/HBList';
import HBListItem from '../HBListItem/HBListItem';
import { HBHighlightText } from '../HBHighlightText';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import {
    SelectRoot,
    SelectPopoverContent,
    ChevronWrapper,
    NoOptionsMessage,
    LiveRegion
} from './HBSelect.styles';
import { HBSelectOption, HBSelectProps } from './HBSelect.types';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

function HBSelect<Value = string>({
    options,
    id,
    value,
    onChange,
    isMulti = false,
    size = 'md',
    placeholder,
    disabled = false,
    error = false,
    clearable = false,
    searchable = true,
    noOptionsMessage = 'No options',
    menuMaxHeight,
    customTheme,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    testId,
    testIdName
}: HBSelectProps<Value> & MustIncludeOneOfTestIdProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // True only while the user is navigating with Arrow keys. aria-activedescendant
    // is spec-correct only for keyboard navigation — mouse hover must not set it,
    // otherwise VoiceOver announces every hovered item and double-counts selected ones.
    const [isKeyboardHighlighted, setIsKeyboardHighlighted] = useState(false);
    const [liveMessage, setLiveMessage] = useState('');

    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const menuId = useRef(uniqueId('hb-select-menu-')).current;

    // Pre-compute so the same testId flows down to HBInput (which would otherwise
    // append its own '-input' suffix and break the driver's testId contracts).
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'select',
        componentName: 'HBSelect'
    });

    // ── Derived selection state ──────────────────────────────────────────────

    const isOptionSelected = useCallback(
        (optValue: Value): boolean => {
            if (isMulti) {
                return ((value as Value[] | undefined) ?? []).some(
                    v => v === optValue
                );
            }
            return optValue === (value as Value | null);
        },
        [isMulti, value]
    );

    // Single-mode display label; multi-mode shows tags via HBInput so no label needed
    const displayLabel = useMemo(
        () =>
            !isMulti
                ? options.find(opt => opt.value === (value as Value | null))
                      ?.label ?? ''
                : '',
        [isMulti, options, value]
    );

    // Multi-mode: pass selected values as HBInput tag chips
    const tags = useMemo(
        () =>
            isMulti
                ? ((value as Value[] | undefined) ?? []).map(v => ({
                      id: String(v),
                      label:
                          options.find(o => o.value === v)?.label ?? String(v)
                  }))
                : undefined,
        [isMulti, options, value]
    );

    const hasClearableValue = isMulti
        ? ((value as Value[] | undefined) ?? []).length > 0
        : (value as Value | null) != null;

    // ── Filtered options ─────────────────────────────────────────────────────

    const filteredOptions = useMemo(
        () =>
            searchable && searchValue
                ? options.filter(opt =>
                      opt.label
                          .toLowerCase()
                          .includes(searchValue.toLowerCase())
                  )
                : options,
        [searchable, searchValue, options]
    );

    // ── Menu open/close ──────────────────────────────────────────────────────

    const openMenu = useCallback(() => {
        if (disabled) {
            return;
        }
        // Single: start highlight at the selected option; multi: no pre-selection
        const startIndex =
            !isMulti && (value as Value | null) != null
                ? filteredOptions.findIndex(
                      o => o.value === (value as Value | null)
                  )
                : -1;
        setIsOpen(true);
        setHighlightedIndex(startIndex);
    }, [disabled, filteredOptions, isMulti, value]);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
        setSearchValue('');
        setHighlightedIndex(-1);
        setIsKeyboardHighlighted(false);
    }, []);

    // ── Option selection ─────────────────────────────────────────────────────

    const selectOption = useCallback(
        (option: HBSelectOption<Value>) => {
            if (option.disabled) {
                return;
            }
            if (isMulti) {
                const currentValues = (value as Value[] | undefined) ?? [];
                const isAlreadySelected = currentValues.some(
                    v => v === option.value
                );
                const newValues = isAlreadySelected
                    ? currentValues.filter(v => v !== option.value)
                    : [...currentValues, option.value];
                (onChange as (v: Value[]) => void)(newValues);
                setSearchValue('');
                // Clear aria-activedescendant without losing the visual highlight
                // position — see isKeyboardHighlighted above.
                setIsKeyboardHighlighted(false);
                // Keep menu open for multi-select
            } else {
                (onChange as (v: Value | null) => void)(option.value);
                closeMenu();
            }
        },
        [isMulti, value, onChange, closeMenu]
    );

    // ── Tag/clear callbacks ──────────────────────────────────────────────────

    const handleTagRemove = useCallback(
        (id: string) => {
            const newValues = ((value as Value[] | undefined) ?? []).filter(
                v => String(v) !== id
            );
            (onChange as (v: Value[]) => void)(newValues);
        },
        [value, onChange]
    );

    const handleClear = useCallback(() => {
        if (isMulti) {
            (onChange as (v: Value[]) => void)([]);
        } else {
            (onChange as (v: Value | null) => void)(null);
        }
        closeMenu();
        inputRef.current?.focus();
    }, [isMulti, onChange, closeMenu]);

    // ── Input event handlers ─────────────────────────────────────────────────

    const handleInputFocus = useCallback(() => {
        openMenu();
    }, [openMenu]);

    const handleInputBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Keep open when focus moves within the popover content (option clicks
            // use onMouseDown preventDefault so blur rarely fires here, but guard
            // for any focusable element that may exist inside the menu).
            if (menuRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }
            if (rootRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }
            closeMenu();
        },
        [closeMenu]
    );

    // Finds the next selectable option index in `direction`, skipping disabled
    // options and wrapping around. Returns `prev` unchanged if every option is
    // disabled, so a fully-disabled list never gets stuck highlighting nothing.
    const findNextEnabledIndex = useCallback(
        (prev: number, direction: 1 | -1) => {
            const length = filteredOptions.length;
            if (length === 0) {
                return -1;
            }
            let index = prev;
            for (let i = 0; i < length; i++) {
                index = (index + direction + length) % length;
                if (!filteredOptions[index]?.disabled) {
                    return index;
                }
            }
            return prev;
        },
        [filteredOptions]
    );

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (!isOpen) {
                        setIsKeyboardHighlighted(true);
                        openMenu();
                        return;
                    }
                    setHighlightedIndex(prev => findNextEnabledIndex(prev, 1));
                    setIsKeyboardHighlighted(true);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (!isOpen) {
                        setIsKeyboardHighlighted(true);
                        openMenu();
                        return;
                    }
                    setHighlightedIndex(prev => findNextEnabledIndex(prev, -1));
                    setIsKeyboardHighlighted(true);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (
                        isOpen &&
                        highlightedIndex >= 0 &&
                        filteredOptions[highlightedIndex]
                    ) {
                        selectOption(filteredOptions[highlightedIndex]);
                        inputRef.current?.focus();
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    closeMenu();
                    break;
                case 'Tab':
                    closeMenu();
                    break;
                default:
                    break;
            }
        },
        [
            isOpen,
            openMenu,
            closeMenu,
            highlightedIndex,
            filteredOptions,
            findNextEnabledIndex,
            selectOption
        ]
    );

    // ── Effects ──────────────────────────────────────────────────────────────

    // Scroll highlighted option into view
    useEffect(() => {
        if (highlightedIndex >= 0) {
            menuRef.current
                ?.querySelector<HTMLElement>(
                    `#${menuId}-option-${highlightedIndex}`
                )
                ?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, menuId]);

    // Announce filtered option count only while the user is actively typing a
    // search query. Announcing on open or on selection races with the screen
    // reader's own "added to selection" announcement and causes incorrect counts.
    useEffect(() => {
        if (!isOpen || !searchValue) {
            setLiveMessage('');
            return;
        }
        if (filteredOptions.length === 0) {
            setLiveMessage(noOptionsMessage);
        } else {
            setLiveMessage(
                `${filteredOptions.length} option${
                    filteredOptions.length === 1 ? '' : 's'
                } available`
            );
        }
    }, [isOpen, searchValue, filteredOptions.length, noOptionsMessage]);

    // ── Render ───────────────────────────────────────────────────────────────

    // When searchable and open: input holds the live search query (starts empty).
    // In single mode, displayLabel is shown as placeholder until the user types.
    const inputValue = isOpen && searchable ? searchValue : displayLabel;
    const inputPlaceholder =
        isOpen && searchable ? displayLabel || placeholder : placeholder;

    // pointer when the field is non-interactive (closed or non-searchable);
    // not-allowed when disabled; undefined lets FieldContainer's text cursor show
    // through (only when searchable + open, i.e. the user is actively typing).
    const selectCursor = disabled
        ? ('not-allowed' as const)
        : !isOpen || !searchable
        ? ('pointer' as const)
        : undefined;

    const activeDescendantId =
        isOpen && highlightedIndex >= 0 && isKeyboardHighlighted
            ? `${menuId}-option-${highlightedIndex}`
            : undefined;

    const handleRootMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) {
                return;
            }
            // Let interactive children (clear button, tag delete buttons) handle their own events
            if ((e.target as HTMLElement).closest('button')) {
                return;
            }
            if (isOpen) {
                // Prevent the blur/refocus cycle and explicitly close
                e.preventDefault();
                closeMenu();
            } else if (document.activeElement === inputRef.current) {
                // Input already focused but menu is closed (e.g. after selection)
                e.preventDefault();
                openMenu();
            }
            // Otherwise let browser set focus → onFocus → openMenu
        },
        [isOpen, disabled, openMenu, closeMenu]
    );

    return (
        <Popover.Root
            open={isOpen}
            onOpenChange={open => {
                if (!open) {
                    closeMenu();
                }
            }}
        >
            <Popover.Anchor asChild>
                <SelectRoot
                    ref={rootRef}
                    $cursor={selectCursor}
                    data-testid={`${finalTestId}--root`}
                    onMouseDown={handleRootMouseDown}
                >
                    <HBInput
                        ref={inputRef}
                        id={id}
                        isNewHBMode
                        testId={finalTestId}
                        size={size}
                        customTheme={customTheme}
                        value={inputValue}
                        readOnly={!searchable || !isOpen}
                        onChange={e => setSearchValue(e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        tags={tags}
                        onTagRemove={isMulti ? handleTagRemove : undefined}
                        suffix={
                            <ChevronWrapper
                                $isOpen={isOpen}
                                $disabled={disabled}
                                aria-hidden="true"
                            >
                                <ArrowDown16 />
                            </ChevronWrapper>
                        }
                        clearable={clearable && hasClearableValue}
                        onClear={handleClear}
                        error={isAriaInvalid(error, ariaInvalid)}
                        disabled={disabled}
                        placeholder={inputPlaceholder}
                        role="combobox"
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy}
                        aria-describedby={ariaDescribedBy}
                        aria-haspopup="listbox"
                        aria-expanded={isOpen}
                        aria-controls={isOpen ? menuId : undefined}
                        aria-activedescendant={activeDescendantId}
                        aria-autocomplete={searchable ? 'list' : 'none'}
                        autoComplete="off"
                    />
                    <LiveRegion aria-live="polite" aria-atomic="true">
                        {liveMessage}
                    </LiveRegion>
                </SelectRoot>
            </Popover.Anchor>
            <Popover.Portal>
                {/*
                 * onOpenAutoFocus / onCloseAutoFocus: prevent Radix from moving
                 * focus into or out of the content — focus must stay on the input
                 * for the aria-activedescendant combobox pattern to work.
                 * onMouseDown: prevent blur so the input stays focused on option clicks.
                 */}
                <SelectPopoverContent
                    ref={menuRef}
                    id={menuId}
                    role="listbox"
                    aria-multiselectable={isMulti || undefined}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    sideOffset={4}
                    align="start"
                    onOpenAutoFocus={e => e.preventDefault()}
                    onCloseAutoFocus={e => e.preventDefault()}
                    onFocusOutside={e => e.preventDefault()}
                    onEscapeKeyDown={e => e.preventDefault()}
                    onMouseDown={e => e.preventDefault()}
                    data-testid={`${finalTestId}--menu`}
                >
                    <HBList
                        disableKeyboardNavigation
                        maxHeight={menuMaxHeight ?? '240px'}
                    >
                        {filteredOptions.length === 0 ? (
                            <NoOptionsMessage
                                paddingX="12"
                                paddingY="0"
                                height="40px"
                                cursor="default"
                                userSelect="none"
                            >
                                <HBFlex height="100%">
                                    {noOptionsMessage}
                                </HBFlex>
                            </NoOptionsMessage>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected = isOptionSelected(
                                    option.value
                                );
                                return (
                                    <HBListItem
                                        key={String(option.value)}
                                        id={`${menuId}-option-${index}`}
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-disabled={
                                            option.disabled || undefined
                                        }
                                        selected={isSelected}
                                        disabled={option.disabled}
                                        selectable={
                                            isMulti ? 'multi' : 'single'
                                        }
                                        size="md"
                                        prefix={option.prefix}
                                        data-highlighted={
                                            index === highlightedIndex
                                                ? ''
                                                : undefined
                                        }
                                        onClick={() => {
                                            selectOption(option);
                                            // In multi mode, keep focus in input
                                            // for continued selection. In single
                                            // mode, closeMenu() handles it and
                                            // calling focus() would reopen the menu
                                            // via handleInputFocus.
                                            if (
                                                isMulti &&
                                                document.activeElement !==
                                                    inputRef.current
                                            ) {
                                                inputRef.current?.focus();
                                            }
                                        }}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(index);
                                            setIsKeyboardHighlighted(false);
                                        }}
                                        data-testid={`${finalTestId}--option-${String(
                                            option.value
                                        )}`}
                                    >
                                        {searchable && searchValue ? (
                                            <HBHighlightText
                                                query={searchValue}
                                            >
                                                {option.label}
                                            </HBHighlightText>
                                        ) : (
                                            option.label
                                        )}
                                    </HBListItem>
                                );
                            })
                        )}
                    </HBList>
                </SelectPopoverContent>
            </Popover.Portal>
        </Popover.Root>
    );
}

export default HBSelect;
